import type { RiskProfile } from '@/features/clients/types/schemas';
import type { ConcernKey } from '@/features/sessions/constants/concerns';
import { apiClient } from '@/lib/api-client';

import {
  chatMessageSchema,
  chatMessagesPaginationSchema,
  chatSessionSchema,
  createdChatSessionSchema,
  endedChatSessionSchema,
  typingSignalSchema,
  type ChatMessage,
  type ChatMessagesPage,
  type ChatSession,
  type ChatSessionStatus,
  type CreatedChatSession,
  type EndedChatSession,
  type TypingSignal,
} from '../types/schemas';

interface Envelope<T> {
  status: 'success' | 'error';
  message?: string;
  data: T;
  pagination?: unknown;
}

export const chatService = {
  /**
   * POST /chat-sessions: start (or resume) the caller's chat. The BE returns
   * 201 with a new chat or 200 with the already-active one; callers just
   * navigate to `data.id` either way.
   * BE rate limit: 5/min/user.
   */
  async create(): Promise<CreatedChatSession> {
    const { data } = await apiClient.post<Envelope<unknown>>('/chat-sessions');
    return createdChatSessionSchema.parse(data.data);
  },

  /**
   * GET /chat-sessions?status=active|closed: role-scoped list, newest
   * activity first (BE orders by COALESCE(lastMessageAt, createdAt) desc).
   * BE rate limit: 30/min/user.
   */
  async list({
    status,
    locationIds,
  }: { status?: ChatSessionStatus; locationIds?: string[] } = {}): Promise<ChatSession[]> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (locationIds && locationIds.length > 0) params.locationIds = locationIds.join(',');
    const { data } = await apiClient.get<Envelope<unknown[]>>('/chat-sessions', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return data.data.map((row) => chatSessionSchema.parse(row));
  },

  /** GET /chat-sessions/:id, same enriched shape as a list row. */
  async get(chatSessionId: string): Promise<ChatSession> {
    const { data } = await apiClient.get<Envelope<unknown>>(`/chat-sessions/${chatSessionId}`);
    return chatSessionSchema.parse(data.data);
  },

  /**
   * GET /chat-sessions/:id/messages: oldest first, decrypted server side.
   * Default page of 200 renders a typical conversation in one fetch.
   */
  async listMessages({
    chatSessionId,
    limit,
    offset,
  }: {
    chatSessionId: string;
    limit?: number;
    offset?: number;
  }): Promise<ChatMessagesPage> {
    const { data } = await apiClient.get<Envelope<unknown[]>>(
      `/chat-sessions/${chatSessionId}/messages`,
      {
        params: { ...(limit !== undefined && { limit }), ...(offset !== undefined && { offset }) },
      },
    );
    return {
      messages: data.data.map((row) => chatMessageSchema.parse(row)),
      pagination: chatMessagesPaginationSchema.parse(data.pagination),
    };
  },

  /** POST /chat-sessions/:id/messages: send a message (1..4000 chars). */
  async sendMessage({
    chatSessionId,
    content,
  }: {
    chatSessionId: string;
    content: string;
  }): Promise<ChatMessage> {
    const { data } = await apiClient.post<Envelope<unknown>>(
      `/chat-sessions/${chatSessionId}/messages`,
      { content },
    );
    return chatMessageSchema.parse(data.data);
  },

  /**
   * POST /chat-sessions/:id/typing: ephemeral typing ping, no body, no DB
   * write. Callers throttle to one POST per ~3s while the composer is active.
   */
  async typing(chatSessionId: string): Promise<TypingSignal> {
    const { data } = await apiClient.post<Envelope<unknown>>(
      `/chat-sessions/${chatSessionId}/typing`,
    );
    return typingSignalSchema.parse(data.data);
  },

  /**
   * POST /chat-sessions/:id/end: close the chat. `concerns` and `riskProfile`
   * are only meaningful from psychologist / chief callers; students omit
   * them. `riskProfile` writes the client's risk in the same call (the BE
   * requires it when the client has none yet). Returns the raw closed row
   * (not the enriched list shape).
   */
  async end({
    chatSessionId,
    concerns,
    riskProfile,
  }: {
    chatSessionId: string;
    concerns?: ConcernKey[];
    riskProfile?: RiskProfile;
  }): Promise<EndedChatSession> {
    const { data } = await apiClient.post<Envelope<unknown>>(
      `/chat-sessions/${chatSessionId}/end`,
      {
        ...(concerns !== undefined && { concerns }),
        ...(riskProfile !== undefined && { riskProfile }),
      },
    );
    return endedChatSessionSchema.parse(data.data);
  },

  /**
   * PATCH /chat-sessions/:id/concerns: retrospectively set the concerns on an
   * existing chat (psychologist / chief only). The full array replaces the
   * stored one; an empty array clears it back to "Not assigned". Returns the
   * raw row (same minimal shape as `end`).
   */
  async updateConcerns({
    chatSessionId,
    concerns,
  }: {
    chatSessionId: string;
    concerns: ConcernKey[];
  }): Promise<EndedChatSession> {
    const { data } = await apiClient.patch<Envelope<unknown>>(
      `/chat-sessions/${chatSessionId}/concerns`,
      { concerns },
    );
    return endedChatSessionSchema.parse(data.data);
  },
};
